import React, { useRef, useState } from "react";
import api from "../../../../services/config";
import {
  getPresignedGetUrlHistoria,
  getPresignedGetUrlConsulta,
  getPresignedUrlHistoria,
  getPresignedUrlConsulta,
  putFileToS3,
  confirmUploadHistoria,
  confirmUploadConsulta,
  openPresignedUrl,
} from "../../../../services/labsS3";

const BASE_API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDateTime = (fecha) => {
  if (!fecha) return "—";
  try {
    return new Date(fecha).toLocaleString("es-MX", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const EstudiosPaciente = ({ historiaClinica, readOnly = false }) => {
   const fileInputRefs = useRef({});
  const [subiendoBloque, setSubiendoBloque] = useState(null);

  const getBloqueKey = (consulta) =>
    consulta.consultaId
      ? `consulta-${consulta.consultaId}`
      : `historia-${consulta.historiaId}`;

  const abrirSelectorArchivo = (consulta) => {
    const bloqueKey = getBloqueKey(consulta);
    fileInputRefs.current[bloqueKey]?.click();
  };

  const subirArchivoSeleccionado = async (consulta, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file) return;

    const bloqueKey = getBloqueKey(consulta);

    try {
      setSubiendoBloque(bloqueKey);

      if (consulta.consultaId) {
        const { key, upload_url } = await getPresignedUrlConsulta(
          consulta.consultaId,
          file
        );

        await putFileToS3(upload_url, file);
        await confirmUploadConsulta(consulta.consultaId, key, file);
      } else {
        const { key, upload_url } = await getPresignedUrlHistoria(
          consulta.historiaId,
          file
        );

        await putFileToS3(upload_url, file);
        await confirmUploadHistoria(consulta.historiaId, key, file);
      }

      alert("Archivo subido correctamente.");
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("No se pudo subir el archivo.");
    } finally {
      setSubiendoBloque(null);
    }
  };

  if (!historiaClinica) {
    return <div className="consulta-empty">Sin consultas previas</div>;
  }

  /* =====================================================
     ARMADO DE CONSULTAS (MISMO CRITERIO QUE LEGACY)
     Consulta 1 = Historia clínica inicial
     Consulta 2+ = Seguimientos
     ===================================================== */

  const consultaInicial = {
    numero: 1,
    tipo: "Consulta inicial",
    fecha:
      historiaClinica.fechaConsulta ||
      historiaClinica.fecha_registro ||
      historiaClinica.creado_en,
    laboratorios: Array.isArray(historiaClinica.laboratorios)
      ? historiaClinica.laboratorios
      : [],
    laboratorios_texto: historiaClinica.laboratorios_texto,
    historiaId: historiaClinica.id,
  };

  const seguimientosRaw = Array.isArray(historiaClinica.consultas)
    ? [...historiaClinica.consultas]
    : [];

  // 1) Orden cronológico para asignar números (Consulta 2, 3, 4...)
    const seguimientosOrdenadosAsc = seguimientosRaw.sort((a, b) => {
    const fa = new Date(a.fechaConsulta || a.fecha_consulta || a.creado_en);
    const fb = new Date(b.fechaConsulta || b.fecha_consulta || b.creado_en);
    return fa - fb;
  });

  const seguimientos = seguimientosOrdenadosAsc.map((c, index) => ({
    numero: index + 2,
    tipo: "Seguimiento",
    fecha: c.fechaConsulta || c.fecha_consulta || c.creado_en,
    laboratorios: Array.isArray(c.laboratorios) ? c.laboratorios : [],
    laboratorios_texto: c.laboratorios_texto,
    consultaId: c.id,
  }));

  const timeline = [...seguimientos, consultaInicial].sort(
    (a, b) => new Date(b.fecha) - new Date(a.fecha)
  );

  /* =====================================================
     DELETE LAB — HISTORIA
     ===================================================== */
  const deleteLabHistoria = async (historiaId, lab) => {
    if (
      !window.confirm(
        "¿Seguro que deseas eliminar este archivo del expediente clínico? Esta acción no se puede deshacer."
      )
    ) {
      return;
    }

    try {
      if (lab.key) {
        await api.post(
          `/historias-clinicas/${historiaId}/labs/delete/`,
          { lab_id: lab.id }
        );
      } else {
        await api.delete(
          `/historias-clinicas/${historiaId}/delete_laboratorio/`,
          { params: { archivo: lab.nombre } }
        );
      }
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Error eliminando archivo");
    }
  };

  /* =====================================================
     DELETE LAB — CONSULTA
     ===================================================== */
  const deleteLabConsulta = async (consultaId, lab) => {
    if (
      !window.confirm(
        "¿Seguro que deseas eliminar este archivo del expediente clínico? Esta acción no se puede deshacer."
      )
    ) {
      return;
    }

    try {
      if (lab.key) {
        await api.post(
          `/historias-clinicas/${historiaClinica.id}/consultas/${consultaId}/labs/delete/`,
          { lab_id: lab.id }
        );
      } else {
        await api.delete(`/consultas/${consultaId}/delete_laboratorio/`, {
          params: { archivo: lab.nombre },
        });
      }
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("Error eliminando archivo");
    }
  };

  /* =====================================================
     RENDER
     ===================================================== */

  return (
    <div className="consultas-timeline">
      {timeline.map((consulta, idx) => (
        <div key={idx} className="bloque-consulta">
          <div className="bloque-consulta-header">
            <div className="bloque-consulta-titulo">
              Consulta {consulta.numero} · {formatDateTime(consulta.fecha)}
            </div>
            <div className="bloque-consulta-resumen">
              {consulta.tipo}
            </div>
          </div>

          <div className="bloque-consulta-contenido">
            <p><strong>{consulta.laboratorios_texto || " "}</strong></p>
             {!readOnly && (
                         <>
                          <input
                          type="file"
                          style={{ display: "none" }}
                          accept=".pdf,.docx,.jpg,.jpeg,.png"
                          ref={(el) => {
                            fileInputRefs.current[getBloqueKey(consulta)] = el;
                          }}
                          onChange={(e) => subirArchivoSeleccionado(consulta, e)}
                        />

                        <div className="lab-acciones" style={{ marginBottom: "12px" }}>
                          <button
                            type="button"
                            className="lab-action-btn"
                            onClick={() => abrirSelectorArchivo(consulta)}
                            disabled={subiendoBloque === getBloqueKey(consulta)}
                          >
                            {subiendoBloque === getBloqueKey(consulta)
                              ? "Subiendo..."
                              : "⬆ Subir nuevos"}
                          </button>
                        </div>
                        </>
                        )}
                        {consulta.laboratorios.length === 0 && (
                          <div className="lab-empty">
                            Sin estudios adjuntos
                          </div>
                        )}
                           

            {consulta.laboratorios.map((lab, i) => (
              <div key={i} className="lab-item">
                <div className="lab-info">
                  <div className="lab-nombre">
                    📎 {lab.nombre}
                  </div>
                  <div className="lab-fecha">
                    Subido: {formatDateTime(lab.creado_en || lab.fecha)}
                  </div>
                </div>

                <div className="lab-acciones">
                  {/* ⬇ DESCARGAR */}
                  <button
                  className="lab-action-btn"
                    onClick={async () => {
                      // Legacy
                      if (!lab.key && lab.ruta) {
                        window.open(
                          `${BASE_API_URL}api/${
                            consulta.consultaId
                              ? "consultas"
                              : "historias-clinicas"
                          }/descargar-laboratorio/?ruta=${lab.ruta}`,
                          "_blank"
                        );
                        return;
                      }

                      // S3
                      try {
                        const url = consulta.consultaId
                          ? await getPresignedGetUrlConsulta(
                              consulta.consultaId,
                              lab.key
                            )
                          : await getPresignedGetUrlHistoria(
                              consulta.historiaId,
                              lab.key
                            );

                        openPresignedUrl(url);
                      } catch (err) {
                        console.error(err);
                        alert("No se pudo descargar el archivo");
                      }
                    }}
                  >
                    ⬇ Descargar
                  </button>

                  {/* ❌ ELIMINAR */}
                  <button
                   className="lab-action-btn"
                    onClick={() =>
                      consulta.consultaId
                        ? deleteLabConsulta(consulta.consultaId, lab)
                        : deleteLabHistoria(consulta.historiaId, lab)
                    }
                  >
                    ❌ Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default EstudiosPaciente;
